import type { ICellRendererParams } from 'ag-grid-community';
import type { BrandSourceItem } from '../types';
import { BrandFavicon } from '../components/BrandFavicon';
import { SourceDisplayTypeBadge } from '../components/sources/SourceDisplayTypeBadge';
import {
  formatSourceCount,
  formatSourceDate,
  formatSourcePercent,
  sourceDisplayLabel,
} from '../utils/source-insights';

export function brandSourcesColumnConfig() {
  return [
    {
      field: 'host',
      title: 'منبع',
      type: 'string',
      flex: 1,
      minWidth: 220,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) => {
        const row = params.data;
        if (!row) return null;
        const label = sourceDisplayLabel(row.host, row.displayName);
        return (
          <span className="d-flex align-items-center gap-2">
            <BrandFavicon host={row.host} name={label} size="sm" />
            <span className="min-w-0">
              <span className="d-block text-truncate fw-medium text-body">{label}</span>
              <span className="d-block text-truncate fs--2 text-body-tertiary">{row.host}</span>
            </span>
          </span>
        );
      },
    },
    {
      field: 'displayType',
      title: 'نوع',
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) =>
        params.data ? <SourceDisplayTypeBadge type={params.data.displayType} /> : null,
    },
    {
      field: 'usedPercent',
      title: 'استفاده‌شده',
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourcePercent(params.data.usedPercent)}
          </span>
        ) : null,
    },
    {
      field: 'citations',
      title: 'Citationها',
      type: 'string',
      width: 110,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourceCount(params.data.citations)}
          </span>
        ) : null,
    },
    {
      field: 'aiResponses',
      title: 'پاسخ‌های AI',
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourceCount(params.data.aiResponses)}
          </span>
        ) : null,
    },
    {
      field: 'avgCitations',
      title: 'میانگین Citation',
      type: 'string',
      width: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourceCount(params.data.avgCitations)}
          </span>
        ) : null,
    },
    {
      field: 'lastSeenAt',
      title: 'آخرین مشاهده',
      type: 'string',
      width: 160,
      searchable: true,
      valueFormatter: (p: { value: string | null }) => formatSourceDate(p.value),
    },
    {
      field: 'urls',
      title: 'URLها',
      type: 'string',
      flex: 1,
      minWidth: 220,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<BrandSourceItem>) => {
        const row = params.data;
        if (!row?.urls[0]) return <span>—</span>;
        return (
          <div className="d-flex flex-column gap-1">
            {row.urls.map((urlItem) => (
              <div key={`${urlItem.aiRequestId}-${urlItem.url}`}>
                <a href={urlItem.url} target="_blank" rel="noreferrer" className="d-block text-truncate">
                  {urlItem.url}
                </a>
                {urlItem.promptText && (
                  <span className="d-block text-truncate fs--2 text-500">{urlItem.promptText}</span>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
  ];
}
